import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/vue";
import { env } from '../environment/environment';
import decodeToken from "@/helpers/decode-token";
import { isDatabaseUnavailableError, markErrorReported, showDatabaseUnavailable } from "./errorHandler";
import { hydrateTemporal } from "./temporal-json";

// following the approach described in this blog post for creating a simple api wrapper
// https://altrim.io/posts/axios-http-client-using-typescript

enum StatusCode {
  Unauthorized = 401,
  Forbidden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
}

const headers: Readonly<Record<string, string | boolean>> = {
  Accept: "application/json",
  "Content-Type": "application/json; charset=utf-8",
  "Access-Control-Allow-Credentials": true,
  "X-Requested-With": "XMLHttpRequest",
};

const normalizeRequestPath = (url?: string): string => {
  const path = url?.split("?")[0]?.replace(/\/+$/, "").toLowerCase() || "/";
  return path.split("/").map(segment =>
    /^\d+$/.test(segment) || /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(segment)
      ? ":id"
      : segment
  ).join("/");
};

const injectToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  try {
    const token = localStorage.getItem("userToken");

    if (token != null) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return config;
  } catch (error: any) {
    throw new Error(error);
  }
};

const isExpectedUnauthorized = (error: any, status?: number) => {
  if (status !== StatusCode.Unauthorized) return false;

  const path = error?.config?.url?.split("?")[0]?.replace(/\/+$/, "").toLowerCase();
  if (path === "/authentication" || path === "/authentication/refresh") return true;

  const authorization = error?.config?.headers?.get?.("Authorization")
    ?? error?.config?.headers?.Authorization
    ?? error?.config?.headers?.authorization;
  const token = typeof authorization === "string" && authorization.startsWith("Bearer ")
    ? authorization.slice(7)
    : localStorage.getItem("userToken");
  if (!token) return false;

  try {
    const { exp } = decodeToken<{ exp?: number }>(token);
    return typeof exp === "number" && Date.now() >= exp * 1000;
  } catch {
    return false;
  }
};

class Api {
  private instance: AxiosInstance | null = null;

  private get api(): AxiosInstance {    
      return this.instance != null ? this.instance : this.initApi();
  }

  initApi() {
    const api = axios.create({
      baseURL: `${env.VUE_APP_API_URL}/`,
      headers,
      withCredentials: true,
    });

    api.interceptors.request.use(injectToken, (error) => Promise.reject(error));

    api.interceptors.response.use(
      (response: any) => {
        response.data = hydrateTemporal(response.data);
        return response;
      },
      (error: any) => {
        const response = error?.response;

        if (isDatabaseUnavailableError(response)) {
          markErrorReported(error);
          markErrorReported(response);
          showDatabaseUnavailable();
        } else if (!isExpectedUnauthorized(error, response?.status)) {
          Sentry.withScope(scope => {
            const method = error?.config?.method?.toUpperCase() || "UNKNOWN";
            const path = error?.config?.url?.split("?")[0];
            scope.setTag("failure.kind", response ? "http" : "network");
            scope.setFingerprint([
              "api-request",
              method,
              normalizeRequestPath(path),
              response ? String(response.status) : "network",
            ]);
            scope.setContext("request", {
              method,
              path,
              status: response?.status,
            });
            Sentry.captureException(error);
            markErrorReported(error);
            markErrorReported(response);
          });
        } else {
          markErrorReported(error);
          markErrorReported(response);
        }

        return this.handleError(response ?? error);
      }
    );    

    this.instance = api;
    return api;
  }

  request<T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig) {
    return this.api.request<T, R>(config);
  }

  get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig) {
    return this.api.get<T, R>(url, config);
  }

  post<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig
  ) {
    return this.api.post<T, R>(url, data, config);
  }

  put<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig
  ) {
    return this.api.put<T, R>(url, data, config);
  }

  delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig) {
    return this.api.delete<T, R>(url, config);
  }

  // Handle global app errors
  // We can handle generic app errors depending on the status code  
  private handleError(error: any) {
    if(error && error?.status){
      const { status } = error;

      switch (status) {
        case StatusCode.InternalServerError: {
          // Handle InternalServerError
          break;
        }
        case StatusCode.Forbidden: {
          // Handle Forbidden
          break;
        }
        case StatusCode.Unauthorized: {
          if(window.location.pathname !== '/login'){
            const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            window.location.href = `/login?redirect=${encodeURIComponent(redirect)}`;
          }
          break;
        }
        case StatusCode.TooManyRequests: {
          // Handle TooManyRequests
          break;
        }
      }
    }

    return Promise.reject(error);
  }
}

export const api = new Api();
